import {
  applySnapshot,
  flow,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import { Requests } from '../../http-requests';
import { IParams } from '../../http-requests/types';
import { IDepartment, IPosition, IWorker } from '../../types';
import { ISelectValue } from 'components/ui';
import { IKindsExecutorObj } from '../../views/authorized/pages/requests/tabs/auto-parsing/types';
import { RequestStateModel } from './general-models';

/*
  Авторазбор заявок. Коллекция RequestAutoSelectExecutorBinds. Категории и подкатегории хранятся отдельными
  объектами (KindsExecutorModel). Для изменения любого объекта kinds_executor, необходимо отправлять их всех.
  Для категорий, имеющих подкатегории, может храниться описание отдельным объектом с пустыми workers и alarm_emails.
  Некоторые view's моделей принимают параметрами модели из workersStore и requestsStore.
*/

export type CategoryItemType = Instance<typeof CategoryItemModel>;
export type SettingsModelType = Instance<typeof SettingsModel>;

const StatusesModel = types.model({
  autoDispatchCategories: types.optional(RequestStateModel, {}),
  editCategoryItem: types.optional(RequestStateModel, {}),
  deleteCategoryItem: types.optional(RequestStateModel, {}),
});

const AlarmEmailsModel = types.model({
  email: types.optional(types.string, ''),
  // executor_name: types.optional(types.string, ''), // TODO останется ли ФИО?
});

const KindsExecutorModel = types.model({
  kind: types.optional(types.string, ''),
  workers: types.optional(types.array(types.string), []),
  description: types.optional(types.maybeNull(types.string), null),
  alarm_emails: types.optional(types.array(AlarmEmailsModel), []),
});

const RequestAutoSelectsModel = types.model({
  id: types.optional(types.string, ''),
  provider: types.optional(types.string, ''),
  kinds_executor: types.optional(types.array(KindsExecutorModel), []),
});

const SettingsModel = types.model({
  subcategory: types.optional(types.union(types.string, types.null), ''),
  employee_ids: types.optional(types.array(types.string), []),
  emails_for_notification: types.optional(types.array(types.string), []),
});

const CategoryItemModel = types.model({
  _id: types.optional(types.string, ''),
  description: types.maybeNull(types.string),
  category: types.optional(types.string, ''),
  settings: types.optional(types.array(SettingsModel), []),
});

const NewCategoryModel = types.model({
  category: types.optional(types.string, ''),
  description: types.optional(types.string, ''),
});

export const RequestsAutoParsingStore = types
  .model({
    autoSelects: types.optional(RequestAutoSelectsModel, {}),
    autoDispatchCategories: types.optional(types.array(CategoryItemModel), []),
    newCategory: types.optional(NewCategoryModel, {}),
    currentCategory: types.optional(types.frozen<ISelectValue>(), {
      label: '',
      value: '',
    }),
    currentSubCategory: types.optional(
      types.frozen<{ [key: string]: any }>(),
      {}
    ),
    newKindsExecutors: types.optional(types.array(KindsExecutorModel), []),
    statuses: types.optional(StatusesModel, {}),
  })
  .views((self) => ({
    /* Получение категорий по kinds_executor.kind  */
    showCurrentCategories(categoriesList: ISelectValue[]) {
      const currentCategoriesSet = new Set(
        self.autoDispatchCategories.map((item) => item.category)
      );

      return categoriesList.filter((item) =>
        currentCategoriesSet.has(item.value)
      );
    },
    /* Получение категорий, не содержащихся в kinds_executor (доступные опции при добавлении категории) */
    getAvailableToAddingCategories(categories: ISelectValue[]) {
      const excludedCategories = new Set(
        self.autoDispatchCategories.map((item) => item.category)
      );

      return categories.filter((item) => !excludedCategories.has(item.value));
    },
    // Категория или подкатегория из KindTree с workers и alarm_emails из kinds_executor
    getCategoryOrSubCategoryWithWorkersAndEmails(category: ISelectValue) {
      const categoryInDispatchList = self.autoDispatchCategories.find(
        (item) => item.category === category.value
      );

      if (!categoryInDispatchList || !categoryInDispatchList.settings) {
        return null;
      }

      const result = categoryInDispatchList.settings.map((item) => ({
        categoryValue: !item.subcategory ? category.value : item.subcategory,
        employee_ids: item.employee_ids,
        emails_for_notification: item.emails_for_notification,
      }));

      return result.length ? result : null;
    },
    // Получение по workers id их объектов из дерева departmentPublicTree (+ их positions и departments без дубликатов)
    getFromWorkersToDepartments(
      departmentPublicTree: any[], // c IDepartment будет ругаться, т.к. departmentPublicTree берется из workersStore
      currentKindWorkersIds: string[]
    ) {
      const fromWorkerToDepartmentObj: {
        allDepartments: IDepartment[];
        allPositions: IPosition[];
        allWorkers: IWorker[];
      } = {
        allDepartments: [],
        allPositions: [],
        allWorkers: [],
      };
      currentKindWorkersIds.forEach((workerId: string) => {
        const findedDepartment = departmentPublicTree.find(
          (department: IDepartment) => {
            const findedPosition = department.positions.find((position) => {
              const findedWorker = position.workers.find(
                (worker) => worker.id === workerId
              );

              if (findedWorker) {
                fromWorkerToDepartmentObj.allWorkers.push(findedWorker);

                return findedWorker;
              }
            });

            if (findedPosition) {
              fromWorkerToDepartmentObj.allPositions = Array.from(
                new Set([
                  ...fromWorkerToDepartmentObj.allPositions,
                  findedPosition,
                ])
              ); // без дубликатов

              return findedPosition;
            }
          }
        );

        if (findedDepartment) {
          fromWorkerToDepartmentObj.allDepartments = Array.from(
            new Set([
              ...fromWorkerToDepartmentObj.allDepartments,
              findedDepartment,
            ])
          );
        }
      });

      return fromWorkerToDepartmentObj;
    },
    // Получение по worker id его объекта из дерева departmentPublicTree (+ его position, department)
    getFromWorkerToDepartment(
      departmentPublicTree: any[], // c IDepartment будет ругаться, т.к. departmentPublicTree берется из workersStore
      currentKindWorkerId: string
    ) {
      const fromWorkerToDepartmentObj: {
        department: IDepartment;
        position: IPosition;
        worker: IWorker;
      } = {
        department: {} as IDepartment,
        position: {} as IPosition,
        worker: {} as IWorker,
      };
      const findedDepartment = departmentPublicTree.find(
        (department: IDepartment) => {
          const findedPosition = department.positions.find((position) => {
            const findedWorker = position.workers.find(
              (worker) => worker.id === currentKindWorkerId
            );

            if (findedWorker) {
              fromWorkerToDepartmentObj.worker = findedWorker;

              return findedWorker;
            }
          });

          if (findedPosition) {
            fromWorkerToDepartmentObj.position = findedPosition;

            return findedPosition;
          }
        }
      );

      if (findedDepartment) {
        fromWorkerToDepartmentObj.department = findedDepartment;
      }

      return fromWorkerToDepartmentObj;
    },
    /*
      В kinds_executor может храниться отдельный объект категории с описанием (без workers). Например
      если добавлено описание категории, а работники назначены на подкатегории данной категории.
    */
    // findCategoryDescriptionInKindsExecutor(categoryId: string) {
    //   const kindObjWithDescription = self.autoSelects.kinds_executor.find(
    //     (kindObj: IKindsExecutorObj) =>
    //       kindObj.kind === categoryId && kindObj.description
    //   );
    //   return kindObjWithDescription?.description || '';
    // },

    findCurrentCategoryDescription(categoryValue: string) {
      const categoryDescription = self.autoDispatchCategories.find(
        (item) => categoryValue === item.category
      )?.description;

      return categoryDescription || '';
    },
    getKindsExecutorWithUpdatedCategoryDescription() {
      const categoryToUpdateIndex = self.autoSelects.kinds_executor.findIndex(
        (kindObj: IKindsExecutorObj) =>
          kindObj.kind === self.currentCategory.value
      );
      const kindsExecutorCopy = [...self.autoSelects.kinds_executor];

      if (categoryToUpdateIndex >= 0) {
        kindsExecutorCopy.splice(categoryToUpdateIndex, 1, {
          ...self.autoSelects.kinds_executor[categoryToUpdateIndex],
          description: self.newCategory.description,
        });
      }

      return kindsExecutorCopy;
    },
    /*
      При попытке добавить ответственных лиц в уже присутствующую в kinds_executor категорию/подкатегорию,
      они будут приплюсованы к уже имеющимся. В ином случае будет создан отдельный объект в kinds_executor.
    */
    getKindsExecutorWithUpdates(
      workersIds: string[],
      subCategoriesIds: string[] | string,
      addedEmails: string[] | null,
      withDelete?: boolean
    ) {
      const copyKindsExecutor = [
        ...getSnapshot(self.autoSelects.kinds_executor),
      ];

      const updateKindsExecutor = (
        kindsExecutorArr: IKindsExecutorObj[],
        kindId: string
      ) => {
        const existingKindIndex = copyKindsExecutor.findIndex(
          (kindObj) => kindObj.kind === kindId
        );
        existingKindIndex >= 0
          ? copyKindsExecutor.splice(existingKindIndex, 1, {
              ...copyKindsExecutor[existingKindIndex],
              workers: withDelete
                ? workersIds
                : Array.from(
                    new Set([
                      ...copyKindsExecutor[existingKindIndex].workers,
                      ...workersIds,
                    ]) // без возможности добавить дубликат
                  ),
              ...(addedEmails !== null && {
                alarm_emails: withDelete
                  ? addedEmails.map((email) => ({
                      email,
                    }))
                  : Array.from(
                      new Set([
                        ...copyKindsExecutor[existingKindIndex].alarm_emails,
                        ...addedEmails.map((email) => ({
                          email,
                        })),
                      ]) // без возможности добавить дубликат
                    ),
              }),
            })
          : // Новая подкатегория (новую категорию через "Назначить ответственного" создать невозможно)
            copyKindsExecutor.push({
              kind: kindId,
              description: null,
              alarm_emails: addedEmails?.length
                ? addedEmails.map((email) => ({
                    email,
                    // executor_name: '',
                  }))
                : [],
              workers: workersIds,
            });
      };
      Array.isArray(subCategoriesIds)
        ? subCategoriesIds.length
          ? subCategoriesIds.forEach((kindId: string) =>
              updateKindsExecutor(copyKindsExecutor, kindId)
            )
          : updateKindsExecutor(copyKindsExecutor, self.currentCategory.value)
        : updateKindsExecutor(copyKindsExecutor, subCategoriesIds);

      return copyKindsExecutor;
    },
    getCategoryItem(category: string) {
      return self.autoDispatchCategories.find(
        (item) => item.category === category
      );
    },
  }))
  .actions((self) => ({
    setNewCategory: (payload: { category: string; description: string }) => {
      self.newCategory = payload;
    },
    clearNewCategory: () => {
      self.newCategory = {
        category: '',
        description: '',
      };
    },
    setCurrentCategory: (payload: any) => {
      self.currentCategory = payload;
    },
    setCurrentSubCategory: (payload: SettingsModelType | {}) => {
      self.currentSubCategory = payload;
    },
    getAutoDispatchCategories: flow(function* () {
      try {
        self.statuses.autoDispatchCategories.loadingStates();
        const results = yield Requests.GetAutoDispatchCategories();
        self.autoDispatchCategories = results;
        self.statuses.autoDispatchCategories.successStates();

        return results;
      } catch (error) {
        console.error(error);
        self.statuses.autoDispatchCategories.errorStates();

        // throw error;
      } finally {
        self.statuses.autoDispatchCategories.finallyStates();
      }
    }),
    addCategoryItem: flow(function* (data: IParams) {
      try {
        self.statuses.editCategoryItem.loadingStates();

        const response = yield Requests.addCategoryItem(data);
        self.autoDispatchCategories.push(response);

        self.statuses.editCategoryItem.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.editCategoryItem.errorStates();

        throw error;
      } finally {
        self.statuses.editCategoryItem.finallyStates();
      }
    }),
    updateCategoryItem: flow(function* (
      category: string,
      data: Partial<CategoryItemType>
    ) {
      try {
        const itemToUpdate = self.autoDispatchCategories.find(
          (item) => item.category === category
        );

        if (!itemToUpdate) {
          throw new Error('объект не найден');
        }

        self.statuses.editCategoryItem.loadingStates();
        const response = yield Requests.UpdateRequestsAutoDispatch(
          itemToUpdate._id,
          data
        );
        applySnapshot(itemToUpdate, response);
        self.statuses.editCategoryItem.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.editCategoryItem.errorStates();

        throw error;
      } finally {
        self.statuses.editCategoryItem.finallyStates();
      }
    }),
    deleteCategoryItem: flow(function* (value: string) {
      try {
        const itemToDelete = self.autoDispatchCategories.find(
          (item) => item.category === value
        );

        if (!itemToDelete) {
          throw new Error('объект не найден');
        }

        self.statuses.deleteCategoryItem.loadingStates();
        const response = yield Requests.deleteCategoryItem(itemToDelete._id);
        self.statuses.deleteCategoryItem.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.statuses.deleteCategoryItem.errorStates();

        throw error;
      } finally {
        self.statuses.deleteCategoryItem.finallyStates();
      }
    }),
  }));
